import { SkStyles } from '../styles/SkStyles';
import { safeHex } from '../drawing/svg';
import { isDoorStyle, type DoorStyle } from '../drawing/door';

/**
 * Small helpers shared by the home-page blocks (scene hero, journeys, style
 * cards, split, FAQ, site schema). Kept beside the blocks rather than in
 * `shared/` because only these blocks use them.
 */

/**
 * A heading with at most one phrase set in italic green, as the mockup's
 * `<h1>Your kitchen, <em>made to measure</em>…`. `.sk-heading em` is styled in
 * the base layer. When the phrase is not found in the title the heading renders
 * plain rather than inventing an emphasis.
 */
export function Italicised({ title, emphasis }: { title: string; emphasis?: string }) {
  const phrase = emphasis?.trim();
  if (!phrase) return <>{title}</>;
  const index = title.indexOf(phrase);
  if (index < 0) return <>{title}</>;
  return (
    <>
      {title.slice(0, index)}
      <em>{phrase}</em>
      {title.slice(index + phrase.length)}
    </>
  );
}

/**
 * Background surfaces for the home blocks: the three shared ones plus `stone`,
 * the mockup's `.band` (its second paper tone).
 */
export type HomeSurface = 'white' | 'warm' | 'sage-grey' | 'stone';

export function homeSurfaceClass(value: unknown, fallback: HomeSurface): string {
  const surface: HomeSurface =
    value === 'white' || value === 'warm' || value === 'sage-grey' || value === 'stone' ? value : fallback;
  return `sk-surface-${surface}`;
}

/** The one rule the shared base layer lacks; emitted once per document like every SkStyles sheet. */
export function HomeSurfaceStyles() {
  return <SkStyles id="surface-stone" css=".sk-surface-stone{background:var(--sk-stone)}" />;
}

/** An operator-authored door style, or the fallback when it is not one the drawing library knows. */
export function doorStyleOr(value: unknown, fallback: DoorStyle = 'shaker'): DoorStyle {
  return isDoorStyle(value) ? value : fallback;
}

/** An operator-authored `#rgb`/`#rrggbb` colour, or `undefined` when it is not one. */
export function hexOrUndefined(value: unknown): string | undefined {
  const hex = safeHex(value, '');
  return hex || undefined;
}

/** A drawing dimension in mm: a finite number within [min, max], else the fallback. */
export function dimension(value: unknown, fallback: number, min = 100, max = 3000): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : fallback;
}

/**
 * Serialises structured data for a `<script type="application/ld+json">`.
 *
 * <p>`JSON.stringify` alone is not safe inside a script element: a string
 * containing `</script>` would close it early and let authored text run as
 * markup. Every `<` is written as the JSON escape `<`, which parses back
 * to the same string, so no tag can ever start inside the script.
 */
export function jsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

export function JsonLdScript({ data }: { data: unknown }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(data) }} />;
}
